import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronDown, Download, MessageSquare } from "lucide-react";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { HoverHintTrigger, PageTitleWithHelp } from "@/components/ui/title-help";
import { VoiceToTextMicButton } from "@/components/ui/VoiceToTextMicButton";
import { useAuth } from "@/components/AuthProvider";
import { MrpSkuHoverPeek, useDeferredMrpSkuHoverHints } from "@/components/mrp/MrpSkuHoverPeek";
import { QueueItemPresenceAvatars } from "@/components/QueueItemPresenceAvatars";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError } from "@/lib/api";
import { buildMrpPlanningModeHubPath, mrpSkuPlanningModeLabel } from "@/lib/mrp";
import { ManufacturingLocationChip, manufacturingLocationShortLabel } from "@/lib/manufacturingLocationChip";
import {
  fetchInventoryDiscrepancyAssignees,
  fetchInventoryDiscrepancyRequest,
  fetchInventoryDiscrepancyRequests,
  fetchInventoryDiscrepancySkuContext,
  INVENTORY_DISCREPANCY_ASSIGNEES_QUERY_KEY,
  INVENTORY_DISCREPANCY_QUERY_KEY,
  inventoryDiscrepancyStatusLabel,
  patchInventoryDiscrepancyRequest,
  postInventoryDiscrepancyMessage,
  type InventoryDiscrepancyListRow,
  type SgsInventoryDiscrepancyResolutionKind,
  type SgsInventoryDiscrepancyStatus,
} from "./inventoryDiscrepancyApi";
import { useIntegratorMe } from "@/lib/me-query";
import {
  useHubModuleAdminSettingsAny,
  useHubModuleHasAnyAccessAny,
} from "@/lib/useHubModuleWrite";
import { useQueueItemPresence } from "@/lib/useQueueItemPresence";
import { EM_DASH } from "@/lib/utils";

import {
  inventoryDiscrepancyAssigneePatchMessage,
  inventoryDiscrepancyUserIdsMatch,
} from "./inventoryDiscrepancyAssigneePatch";
import {
  approveResolutionOptionHoverHint,
  approveResolutionOptionLabel,
  type ApproveResolutionKind,
} from "./inventoryDiscrepancyResolutionLabels";
import { attachRunningTotalsToSkuContextRows } from "./inventoryDiscrepancySkuContext";
import { downloadSkuInventoryTransactionContextCsv } from "./skuInventoryTransactionContextCsv";
import { SkuInventoryTransactionContextTable } from "./SkuInventoryTransactionContextTable";

const OPEN_STATUSES: SgsInventoryDiscrepancyStatus[] = ["pending", "investigating"];

const INVENTORY_DISCREPANCY_QUEUE_PATHNAME = "/manufacturing-management/inventory-discrepancies";

const INVENTORY_DISCREPANCY_QUEUE_ACCESS_KEYS = [
  "manufacturing",
  "manufacturing_management",
  "warehouse",
] as const;

const INVENTORY_DISCREPANCY_RESOLVE_ADMIN_KEYS = ["manufacturing_management", "warehouse"] as const;

type InventoryDiscrepancyPatchBody = Parameters<typeof patchInventoryDiscrepancyRequest>[1];

function statusTone(status: SgsInventoryDiscrepancyStatus): "warning" | "neutral" | "success" | "danger" {
  if (status === "pending") return "warning";
  if (status === "investigating") return "neutral";
  if (status === "rejected") return "danger";
  return "success";
}

function formatSignedQuantity(quantity: number): string {
  const sign = quantity > 0 ? "+" : "";
  return `${sign}${String(quantity)}`;
}

function detailLoadErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  return "Request not found.";
}

export function InventoryDiscrepancyRequestsPage(): JSX.Element {
  const canAccessQueue = useHubModuleHasAnyAccessAny(INVENTORY_DISCREPANCY_QUEUE_ACCESS_KEYS);
  const canManageQueue = useHubModuleAdminSettingsAny(INVENTORY_DISCREPANCY_RESOLVE_ADMIN_KEYS);
  const meQuery = useIntegratorMe();
  const { session } = useAuth();
  const { showToast } = useHubToast();
  const currentUserId =
    meQuery.data?.user.id?.trim()
    || session?.user.id?.trim()
    || "";
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("request")?.trim() ?? "";
  const skuFilter = searchParams.get("sku")?.trim() ?? "";
  const [statusFilter, setStatusFilter] = useState<"open" | "all">("open");
  const [pendingFeedbackOnly, setPendingFeedbackOnly] = useState(false);
  const [resolutionNote, setResolutionNote] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [approveMenuOpen, setApproveMenuOpen] = useState(false);
  const [autoSelectReady, setAutoSelectReady] = useState(false);
  const approveMenuRef = useRef<HTMLDivElement>(null);

  const listQuery = useQuery({
    queryKey: [...INVENTORY_DISCREPANCY_QUERY_KEY, "list", statusFilter, pendingFeedbackOnly, skuFilter],
    queryFn: () =>
      fetchInventoryDiscrepancyRequests({
        status: statusFilter === "open" ? OPEN_STATUSES.join(",") : undefined,
        pendingFeedback: pendingFeedbackOnly,
        sku: skuFilter !== "" ? skuFilter : undefined,
        limit: 100,
      }),
    enabled: canAccessQueue,
  });

  const rows = listQuery.data?.rows ?? [];

  const { viewersForEntity } = useQueueItemPresence({
    pathname: INVENTORY_DISCREPANCY_QUEUE_PATHNAME,
    enabled: canAccessQueue,
  });

  const selectedListRow = useMemo(
    () => rows.find((row) => row.id === selectedId) ?? null,
    [rows, selectedId],
  );

  const detailQuery = useQuery({
    queryKey: [...INVENTORY_DISCREPANCY_QUERY_KEY, "detail", selectedId],
    queryFn: () => fetchInventoryDiscrepancyRequest(selectedId),
    enabled: selectedId !== "",
  });

  const skuContextQuery = useQuery({
    queryKey: [
      ...INVENTORY_DISCREPANCY_QUERY_KEY,
      "sku-context",
      selectedListRow?.sku_id,
      selectedListRow?.location_id,
    ],
    queryFn: () =>
      fetchInventoryDiscrepancySkuContext({
        skuId: selectedListRow!.sku_id,
        locationId: selectedListRow!.location_id,
      }),
    enabled: selectedListRow != null,
  });

  const assigneeOptionsQuery = useQuery({
    queryKey: INVENTORY_DISCREPANCY_ASSIGNEES_QUERY_KEY,
    queryFn: () => fetchInventoryDiscrepancyAssignees(),
    enabled: canManageQueue && selectedId !== "",
  });

  useEffect(() => {
    if (!approveMenuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (approveMenuRef.current?.contains(target)) return;
      setApproveMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [approveMenuOpen]);

  useEffect(() => {
    setApproveMenuOpen(false);
  }, [selectedId]);

  useEffect(() => {
    if (detailQuery.data?.request.resolution_note != null) {
      setResolutionNote(detailQuery.data.request.resolution_note);
    }
  }, [detailQuery.data?.request.id, detailQuery.data?.request.resolution_note]);

  const patchMutation = useMutation({
    mutationFn: ({
      requestId,
      body,
    }: {
      requestId: string;
      body: InventoryDiscrepancyPatchBody;
    }) => patchInventoryDiscrepancyRequest(requestId, body),
    onSuccess: (_data, { body }) => {
      void queryClient.invalidateQueries({ queryKey: INVENTORY_DISCREPANCY_QUERY_KEY });
      const assigneeMessage = inventoryDiscrepancyAssigneePatchMessage(
        body.assigned_to_user_id,
        currentUserId,
      );
      if (assigneeMessage != null) {
        showToast({ tone: "success", message: assigneeMessage });
      }
    },
    onError: (error: Error, { body }) => {
      if (body.assigned_to_user_id !== undefined) {
        showToast({ tone: "error", message: error.message });
      }
    },
  });

  function patchRequest(body: InventoryDiscrepancyPatchBody): void {
    if (selectedId === "") {
      return;
    }
    patchMutation.mutate({ requestId: selectedId, body });
  }

  const replyMutation = useMutation({
    mutationFn: (body: string) => postInventoryDiscrepancyMessage(selectedId, body, true),
    onSuccess: () => {
      setReplyBody("");
      void queryClient.invalidateQueries({ queryKey: INVENTORY_DISCREPANCY_QUERY_KEY });
    },
  });

  useEffect(() => {
    if (!listQuery.isSuccess || rows.length === 0) {
      setAutoSelectReady(false);
      return;
    }
    const frame = requestAnimationFrame(() => {
      setAutoSelectReady(true);
    });
    return () => cancelAnimationFrame(frame);
  }, [listQuery.isSuccess, rows.length, statusFilter, pendingFeedbackOnly]);

  useEffect(() => {
    if (!autoSelectReady || rows.length === 0) {
      return;
    }
    const hasValidSelection = selectedId !== "" && rows.some((row) => row.id === selectedId);
    if (hasValidSelection) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("request", rows[0]!.id);
    setSearchParams(next, { replace: true });
  }, [autoSelectReady, rows, searchParams, selectedId, setSearchParams]);

  const selected = detailQuery.data?.request ?? selectedListRow;
  const isOpen =
    selected != null
    && (selected.status === "pending" || selected.status === "investigating");
  const isAssignedToCurrentUser =
    currentUserId !== ""
    && selected?.assigned_to_user_id != null
    && inventoryDiscrepancyUserIdsMatch(selected.assigned_to_user_id, currentUserId);

  const assigneeOptions = useMemo(() => {
    const users = assigneeOptionsQuery.data?.users ?? [];
    const options = users.map((user) => ({
      userId: user.id,
      label: user.display_name?.trim() ? user.display_name : user.id,
    }));
    const currentAssigneeId = selected?.assigned_to_user_id?.trim() ?? "";
    if (
      currentAssigneeId !== ""
      && !options.some((option) => option.userId === currentAssigneeId)
      && selected?.assigned_to_display_name
    ) {
      options.unshift({
        userId: currentAssigneeId,
        label: selected.assigned_to_display_name,
      });
    }
    return options;
  }, [
    assigneeOptionsQuery.data?.users,
    selected?.assigned_to_display_name,
    selected?.assigned_to_user_id,
  ]);

  const planningTypeDisplay = useMemo(() => {
    const planningMode = skuContextQuery.data?.planning_mode ?? null;
    const label = mrpSkuPlanningModeLabel(planningMode);
    const href =
      selected != null ? buildMrpPlanningModeHubPath(selected.sku_id, planningMode) : null;
    return { label, href };
  }, [selected, skuContextQuery.data?.planning_mode]);

  const approveResolutionOptions = useMemo((): ApproveResolutionKind[] => {
    return ["adjustment", "wastage", "prototype"];
  }, []);

  const contextRows = skuContextQuery.data?.rows ?? [];
  const contextRowsWithTotals = useMemo(
    () => attachRunningTotalsToSkuContextRows(contextRows, skuContextQuery.data?.sgs_on_hand ?? null),
    [contextRows, skuContextQuery.data?.sgs_on_hand],
  );

  const queueSkus = useMemo(
    () => rows.map((row) => row.sku_id),
    [rows],
  );
  const { skuHoverHints, hintsLoading, enableHintsFetch } = useDeferredMrpSkuHoverHints(
    queueSkus,
    "inventory-discrepancy-requests",
  );

  const selectedSummary = useMemo(() => {
    if (selected != null) {
      return (
        <span className="inline-flex flex-wrap items-center gap-x-1 font-mono text-sm font-semibold">
          #{selected.id} ·{" "}
          <MrpSkuHoverPeek
            sku={selected.sku_id}
            hint={skuHoverHints?.[selected.sku_id.trim()]}
            hintsLoading={hintsLoading}
            onHoverIntent={enableHintsFetch}
            tab="inventory"
          />
          {" · "}
          {formatSignedQuantity(selected.quantity)}
        </span>
      );
    }
    if (selectedId !== "") {
      const row = rows.find((entry) => entry.id === selectedId);
      if (row != null) {
        return (
          <span className="inline-flex flex-wrap items-center gap-x-1 font-mono text-sm font-semibold">
            #{row.id} ·{" "}
            <MrpSkuHoverPeek
              sku={row.sku_id}
              hint={skuHoverHints?.[row.sku_id.trim()]}
              hintsLoading={hintsLoading}
              onHoverIntent={enableHintsFetch}
              tab="inventory"
            />
            {" · "}
            {formatSignedQuantity(row.quantity)}
          </span>
        );
      }
      return `Request #${selectedId}`;
    }
    return null;
  }, [enableHintsFetch, hintsLoading, rows, selected, selectedId, skuHoverHints]);

  function selectRow(row: InventoryDiscrepancyListRow): void {
    const next = new URLSearchParams(searchParams);
    next.set("request", row.id);
    setSearchParams(next, { replace: true });
  }

  function clearSkuFilter(): void {
    const next = new URLSearchParams(searchParams);
    next.delete("sku");
    setSearchParams(next, { replace: true });
  }

  async function approve(kind: SgsInventoryDiscrepancyResolutionKind): Promise<void> {
    if (selectedId === "") return;
    setApproveMenuOpen(false);
    await patchMutation.mutateAsync({
      requestId: selectedId,
      body: {
        resolution_kind: kind,
        resolution_note: resolutionNote.trim() === "" ? null : resolutionNote.trim(),
      },
    });
  }

  const queueFiltersActive =
    statusFilter !== "open" || pendingFeedbackOnly || skuFilter !== "";

  const queueColumns = useMemo((): HubDataTableColumn<InventoryDiscrepancyListRow>[] => {
    return [
      {
        id: "request",
        header: "Request",
        sortKey: "id",
        sortValue: (row) => row.id,
        cell: (row) => (
          <span className="inline-flex flex-wrap items-center gap-x-1 font-mono text-sm font-semibold text-slate-900">
            #{row.id} ·{" "}
            <MrpSkuHoverPeek
              sku={row.sku_id}
              hint={skuHoverHints?.[row.sku_id.trim()]}
              hintsLoading={hintsLoading}
              onHoverIntent={enableHintsFetch}
            />
          </span>
        ),
      },
      {
        id: "location",
        header: "Location",
        sortKey: "location",
        sortValue: (row) => row.location_id,
        cell: (row) => <ManufacturingLocationChip locationId={row.location_id} />,
      },
      {
        id: "status",
        header: "Status",
        sortKey: "status",
        sortValue: (row) => row.status,
        cell: (row) => (
          <div className="flex flex-wrap gap-1">
            <Badge tone={statusTone(row.status)}>{inventoryDiscrepancyStatusLabel(row.status)}</Badge>
            {row.pending_feedback ? <Badge tone="warning">Pending response</Badge> : null}
          </div>
        ),
      },
      {
        id: "assignee",
        header: "Assignee",
        sortKey: "assignee",
        sortValue: (row) => row.assigned_to_display_name ?? "",
        cell: (row) => (
          <Badge tone={row.assigned_to_display_name ? "neutral" : "warning"}>
            {row.assigned_to_display_name?.trim() || "Unassigned"}
          </Badge>
        ),
      },
      {
        id: "submitted",
        header: "Submitted",
        sortKey: "submittedAt",
        sortValue: (row) => row.submitted_at,
        cell: (row) => (
          <span className="text-xs text-slate-600">
            {formatSignedQuantity(row.quantity)} · {row.submitted_by_display_name ?? "Unknown"} ·{" "}
            {new Date(row.submitted_at).toLocaleString()}
          </span>
        ),
      },
      {
        id: "presence",
        header: "",
        sortValue: () => "",
        cell: (row) => (
          <QueueItemPresenceAvatars people={viewersForEntity(row.id)} className="ml-auto shrink-0" />
        ),
      },
    ];
  }, [enableHintsFetch, hintsLoading, skuHoverHints, viewersForEntity]);

  const queueListError =
    listQuery.error instanceof ApiError
      ? listQuery.error.message
      : listQuery.error instanceof Error
        ? listQuery.error.message
        : null;

  return (
    <div className="space-y-4">
      <header>
        <PageTitleWithHelp hoverHint="Review SGS inventory discrepancies submitted from Picking. Investigate using hub transaction context, reply to the submitter, set investigating status, toggle pending feedback, and approve with Megaventory adjustment or wastage, or close as prototype without a Megaventory post.">
          Inventory discrepancies
        </PageTitleWithHelp>
      </header>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <div className="space-y-4 min-w-0">
          <Card>
            <CardHeader>
              <CardTitle>Filters</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={statusFilter === "open" ? "primary" : "secondary"}
                  onClick={() => setStatusFilter("open")}
                >
                  Open
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={statusFilter === "all" ? "primary" : "secondary"}
                  onClick={() => setStatusFilter("all")}
                >
                  All
                </Button>
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={pendingFeedbackOnly}
                    onChange={(event) => setPendingFeedbackOnly(event.target.checked)}
                  />
                  Pending response
                </label>
              </div>
              {skuFilter !== "" ? (
                <p className="text-sm text-slate-700">
                  Showing requests for <span className="font-mono font-semibold">{skuFilter}</span>
                  <Button type="button" size="sm" variant="ghost" className="ml-2" onClick={clearSkuFilter}>
                    Show all
                  </Button>
                </p>
              ) : null}
            </CardBody>
          </Card>

          <HubDataTable
            title={
              listQuery.isLoading
                ? "Loading queue…"
                : `${rows.length} request${rows.length === 1 ? "" : "s"}`
            }
            columns={queueColumns}
            rows={rows}
            rowKey={(row) => row.id}
            fillViewport={false}
            loading={listQuery.isLoading}
            loadingMessage="Loading…"
            error={canAccessQueue ? queueListError : null}
            empty="No requests match this filter."
            isFiltered={queueFiltersActive || skuFilter !== ""}
            filteredEmpty={
              skuFilter !== ""
                ? `No requests for ${skuFilter} match this filter.`
                : "No requests match this filter."
            }
            onRowClick={selectRow}
            rowClassName={(row) => (selectedId === row.id ? "bg-brand-50" : undefined)}
          />
        </div>

        <Card>
          <CardHeader className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle>{selectedSummary ?? "Select a request"}</CardTitle>
            {selected != null ? (
              canManageQueue && isOpen ? (
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    id="inventory-discrepancy-assignee"
                    aria-label="Assignee"
                    className="h-8 max-w-[11rem] rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    value={selected.assigned_to_user_id ?? ""}
                    disabled={patchMutation.isPending || assigneeOptionsQuery.isLoading}
                    onChange={(event) => {
                      const value = event.target.value;
                      patchRequest({
                        assigned_to_user_id: value === "" ? null : value,
                      });
                    }}
                  >
                    <option value="">Unassigned</option>
                    {assigneeOptions.map((option) => (
                      <option key={option.userId} value={option.userId}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={
                      patchMutation.isPending
                      || (meQuery.isLoading && currentUserId === "")
                      || currentUserId === ""
                      || isAssignedToCurrentUser
                    }
                    onClick={() => patchRequest({ assigned_to_user_id: currentUserId })}
                  >
                    Take over
                  </Button>
                </div>
              ) : (
                <span className="text-sm text-slate-600">
                  {selected.assigned_to_display_name?.trim() || "Unassigned"}
                </span>
              )
            ) : null}
          </CardHeader>
          <CardBody className="space-y-4 border-t border-slate-100">
            {selectedId === "" ? (
              <p className="text-sm text-slate-600">Choose a request from the queue to investigate.</p>
            ) : selected == null && detailQuery.isError ? (
              <p className="text-sm text-rose-700">{detailLoadErrorMessage(detailQuery.error)}</p>
            ) : selected == null ? (
              <p className="text-sm text-rose-700">Request not found.</p>
            ) : (
              <>
                <div className="grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
                  <p>
                    <span className="font-medium text-slate-900">Submitted:</span>{" "}
                    {selected.submitted_by_display_name ?? EM_DASH}
                  </p>
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-slate-900">Location:</span>
                    <ManufacturingLocationChip locationId={selected.location_id} />
                  </p>
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-slate-900">Planning type:</span>
                    {skuContextQuery.isLoading ? (
                      "…"
                    ) : planningTypeDisplay.label != null && planningTypeDisplay.href != null ? (
                      <Link
                        to={planningTypeDisplay.href}
                        className="text-sky-700 hover:text-sky-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus:ring-offset-1"
                      >
                        {planningTypeDisplay.label}
                      </Link>
                    ) : planningTypeDisplay.label != null ? (
                      planningTypeDisplay.label
                    ) : (
                      EM_DASH
                    )}
                  </p>
                  <p>
                    <span className="font-medium text-slate-900">On hand:</span>{" "}
                    {skuContextQuery.isLoading
                      ? "…"
                      : skuContextQuery.data?.sgs_on_hand ?? EM_DASH}
                  </p>
                  <p className="sm:col-span-2">
                    <span className="font-medium text-slate-900">Reason:</span>{" "}
                    {selected.reason?.trim() ? selected.reason : EM_DASH}
                  </p>
                  {selected.carton_box_hex != null && selected.carton_box_hex.trim() !== "" ? (
                    <p className="sm:col-span-2">
                      <span className="font-medium text-slate-900">Carton:</span>{" "}
                      <Link
                        to={`/i/${selected.carton_box_hex}`}
                        className="font-mono text-sky-700 hover:text-sky-900 hover:underline"
                      >
                        {selected.carton_short_code?.trim() || selected.carton_box_hex}
                      </Link>
                      {selected.carton_quantity_before != null && selected.carton_quantity_after != null ? (
                        <>
                          {" "}
                          ({String(selected.carton_quantity_before)} to{" "}
                          {String(selected.carton_quantity_after)})
                        </>
                      ) : null}
                    </p>
                  ) : null}
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-slate-900">Transaction context</h3>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="gap-1"
                      disabled={skuContextQuery.isLoading || contextRowsWithTotals.length === 0}
                      onClick={() =>
                        downloadSkuInventoryTransactionContextCsv(selected.sku_id, contextRowsWithTotals)
                      }
                    >
                      <Download className="h-3.5 w-3.5" aria-hidden />
                      Download CSV
                    </Button>
                  </div>
                  <SkuInventoryTransactionContextTable
                    layout="embedded"
                    rows={contextRowsWithTotals}
                    loading={skuContextQuery.isLoading}
                    loadingMessage="Loading transaction context…"
                    error={
                      skuContextQuery.isError ? detailLoadErrorMessage(skuContextQuery.error) : null
                    }
                    emptyMessage={
                      <>
                        No recent hub transactions found for this SKU at{" "}
                        {manufacturingLocationShortLabel(selected.location_id)}.
                      </>
                    }
                    showRunningTotal={true}
                  />
                </div>

                <div>
                  <h3 className="mb-2 flex items-center gap-1 text-sm font-semibold text-slate-900">
                    <MessageSquare className="h-4 w-4" aria-hidden />
                    Messages
                  </h3>
                  <ul className="max-h-40 space-y-2 overflow-y-auto rounded-md border border-slate-200 p-2 text-sm">
                    {detailQuery.isLoading ? (
                      <li className="text-slate-500">Loading messages…</li>
                    ) : (detailQuery.data?.messages ?? []).length === 0 ? (
                      <li className="text-slate-500">No messages yet.</li>
                    ) : (
                      detailQuery.data?.messages.map((message) => (
                        <li key={message.id} className="rounded-md bg-slate-50 px-2 py-1.5">
                          <p className="text-xs font-medium text-slate-600">
                            {message.author_display_name ?? "User"}
                            {message.is_staff ? " (staff)" : ""} ·{" "}
                            {new Date(message.created_at).toLocaleString()}
                          </p>
                          <p className="whitespace-pre-wrap text-slate-800">{message.body}</p>
                        </li>
                      ))
                    )}
                  </ul>
                  {isOpen ? (
                    <div className="mt-2 flex items-stretch gap-2">
                      <Input
                        value={replyBody}
                        onChange={(event) => setReplyBody(event.target.value)}
                        placeholder="Reply to submitter…"
                      />
                      <VoiceToTextMicButton
                        className="h-10 w-10"
                        value={replyBody}
                        ariaLabel="Dictate reply"
                        onValueChange={setReplyBody}
                      />
                      <Button
                        type="button"
                        disabled={!canManageQueue || replyBody.trim() === "" || replyMutation.isPending}
                        onClick={() => replyMutation.mutate(replyBody.trim())}
                      >
                        Send
                      </Button>
                    </div>
                  ) : null}
                </div>

                {isOpen && canManageQueue ? (
                  <div className="space-y-3 rounded-md border border-slate-200 p-3">
                    <div className="flex flex-wrap gap-4">
                      <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={selected.status === "investigating"}
                          disabled={patchMutation.isPending}
                          onChange={(event) =>
                            patchRequest({
                              status: event.target.checked ? "investigating" : "pending",
                            })
                          }
                        />
                        Investigating
                      </label>
                      <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={selected.pending_feedback}
                          onChange={(event) =>
                            patchRequest({ pending_feedback: event.target.checked })
                          }
                        />
                        Pending response
                      </label>
                    </div>
                    <div>
                      <Label htmlFor="resolution-note">Resolution / rejection note</Label>
                      <div className="mt-1 flex items-stretch gap-2">
                        <Input
                          id="resolution-note"
                          value={resolutionNote}
                          onChange={(event) => setResolutionNote(event.target.value)}
                          placeholder="Notes recorded on approval or rejection"
                        />
                        <VoiceToTextMicButton
                          className="h-10 w-10"
                          value={resolutionNote}
                          ariaLabel="Dictate resolution note"
                          onValueChange={setResolutionNote}
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="relative" ref={approveMenuRef}>
                        <Button
                          type="button"
                          variant="primary"
                          disabled={patchMutation.isPending}
                          aria-haspopup="menu"
                          aria-expanded={approveMenuOpen}
                          className="gap-1"
                          onClick={() => setApproveMenuOpen((open) => !open)}
                        >
                          Approve
                          <ChevronDown className="h-4 w-4" aria-hidden />
                        </Button>
                        {approveMenuOpen ? (
                          <div
                            role="menu"
                            className="absolute left-0 top-full z-20 mt-1 min-w-[10rem] overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
                          >
                            {approveResolutionOptions.map((kind) => {
                              const label = approveResolutionOptionLabel(kind, selected.quantity);
                              const hoverHint = approveResolutionOptionHoverHint(kind, selected.quantity);
                              return (
                                <button
                                  key={kind}
                                  type="button"
                                  role="menuitem"
                                  className="flex w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                                  disabled={patchMutation.isPending}
                                  onClick={() => void approve(kind)}
                                >
                                  {hoverHint != null ? (
                                    <HoverHintTrigger align="start" block trigger={label}>
                                      {hoverHint}
                                    </HoverHintTrigger>
                                  ) : (
                                    label
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={patchMutation.isPending}
                        onClick={() => void approve("reject")}
                      >
                        Reject
                      </Button>
                    </div>
                    {selected.mv_document_no != null ? (
                      <p className="text-xs text-slate-600">
                        Megaventory document: {selected.mv_document_no}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
